/**
 * /sites/:siteId — landing page. The "Needs attention" cockpit (step 24 v1).
 *
 * v1.0 sources (see Ultra Plan v3):
 *   - drafts with status='pending_review' (from _drafts/*.json webhook)
 *   - any draft with blocking lint findings
 *   - cost summary for today
 *
 * KPI tiles + canonical canary deferred to v1.0.5; cron data lands in step 26.
 */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError } from '../lib/api.ts'
import { friendlyError } from '../lib/friendly-errors.ts'
import { StatusDot, statusLabel, StatusLegend } from '../components/StatusDot.tsx'

interface AttentionDraft {
  slug: string
  title: string
  status: string
  lintScore: number | null
  wordCount: number
}

interface CostSummary {
  totalCents: number
  capCents: number
  remainingCents: number
  blocked: boolean
  perProvider: { provider: string; totalCents: number; calls: number }[]
}

interface IndexingRow {
  slug: string
  title: string
  url: string
  publishedAt: number
  indexingApiAt: number | null
  indexingApiOk: number | null
  inspectedAt: number | null
  state: 'indexed' | 'discovered' | 'excluded' | 'unknown'
  verdict: string | null
  coverageState: string | null
  canonicalGoogle: string | null
  canonicalUser: string | null
}

interface IndexingHealth {
  rollup: {
    total: number
    indexed: number
    discovered: number
    excluded: number
    unknown: number
    submittedToIndexingApi: number
    canonicalMismatches: number
  }
  rows: IndexingRow[]
}

interface LinkRow {
  slug: string
  title: string
  outbound: string[]
  inboundCount: number
  hubLinks: string[]
  isOrphan: boolean
  isHubOnly: boolean
}

interface LinksHealth {
  totals: {
    articles: number
    orphans: number
    hubOnly: number
    crossLinked: number
    avgOutbound: number
    avgInbound: number
  }
  rows: LinkRow[]
  hubPaths: string[]
}

export function Cockpit() {
  const { siteId = 'chc' } = useParams()
  const [pending, setPending] = useState<AttentionDraft[] | null>(null)
  const [lowLint, setLowLint] = useState<AttentionDraft[] | null>(null)
  const [cost, setCost] = useState<CostSummary | null>(null)
  const [indexing, setIndexing] = useState<IndexingHealth | null>(null)
  const [indexingErr, setIndexingErr] = useState<string | null>(null)
  const [resubmitBusy, setResubmitBusy] = useState<string | null>(null)
  const [links, setLinks] = useState<LinksHealth | null>(null)
  const [linksErr, setLinksErr] = useState<string | null>(null)
  // Slugs that successfully resubmitted in the last 5s. Used to swap the
  // button label from "Resubmit" → "✓ Submitted" so the click visibly did
  // something. Clears on its own so the row reverts to its normal state.
  const [recentSuccess, setRecentSuccess] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // Pull the full grid once + bucket client-side. Cheap because we already
    // hit /drafts on the Articles screen; v1.0.5 swaps for a dedicated
    // /cockpit endpoint that does the bucketing in SQL.
    api<{ cards: AttentionDraft[] }>(`/api/sites/${siteId}/drafts`)
      .then((res) => {
        if (cancelled) return
        setPending(res.cards.filter((c) => c.status === 'pending_review'))
        setLowLint(
          res.cards
            .filter((c) => c.status !== 'published' && c.lintScore !== null && c.lintScore < 80)
            .sort((a, b) => (a.lintScore ?? 0) - (b.lintScore ?? 0)),
        )
      })
      .catch((err) => {
        if (cancelled) return
        setError(friendlyError(err).message)
      })

    api<CostSummary>(`/api/sites/${siteId}/cost/today`)
      .then((res) => !cancelled && setCost(res))
      .catch(() => {
        /* cost is best-effort */
      })

    api<IndexingHealth>(`/api/sites/${siteId}/indexing`)
      .then((res) => !cancelled && setIndexing(res))
      .catch((err) => {
        if (!cancelled) setIndexingErr(friendlyError(err).message)
      })

    api<LinksHealth>(`/api/sites/${siteId}/links`)
      .then((res) => !cancelled && setLinks(res))
      .catch((err) => {
        if (!cancelled) setLinksErr(friendlyError(err).message)
      })
    return () => {
      cancelled = true
    }
  }, [siteId])

  async function resubmit(slug: string) {
    setResubmitBusy(slug)
    try {
      const res = await api<{ url: string; indexingApi?: { ok: boolean }; indexNow?: { ok: boolean } }>(
        `/api/sites/${siteId}/indexing/resubmit/${slug}`,
        { method: 'POST' },
      )
      const fresh = await api<IndexingHealth>(`/api/sites/${siteId}/indexing`)
      setIndexing(fresh)
      // Mark as recently-succeeded so the button shows confirmation for 5s.
      const okAny =
        (res.indexingApi && res.indexingApi.ok) || (res.indexNow && res.indexNow.ok)
      if (okAny) {
        setRecentSuccess((p) => ({ ...p, [slug]: Date.now() }))
        setTimeout(() => {
          setRecentSuccess((p) => {
            const n = { ...p }
            delete n[slug]
            return n
          })
        }, 5000)
      }
    } catch (err) {
      alert(friendlyError(err).message)
    } finally {
      setResubmitBusy(null)
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{siteId.toUpperCase()} cockpit</h1>
          <div className="subtitle">What needs your attention.</div>
        </div>
        <StatusLegend />
      </div>

      {error && <div className="error-state" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'grid', gap: '1.5rem' }}>
        <CostTile cost={cost} />

        <IndexingPanel
          health={indexing}
          error={indexingErr}
          resubmitBusy={resubmitBusy}
          recentSuccess={recentSuccess}
          onResubmit={resubmit}
        />

        <LinksPanel siteId={siteId} health={links} error={linksErr} />

        <Panel
          title="Pending AI co-author drafts"
          subtitle="Imported via _drafts/*.json webhook — review + accept or kick back."
          drafts={pending}
          siteId={siteId}
          emptyHint="No pending review drafts."
        />

        <Panel
          title="Low lint score (under 80)"
          subtitle="Open drafts with blocking findings or many warnings."
          drafts={lowLint}
          siteId={siteId}
          emptyHint="All open drafts are above 80."
        />
      </div>
    </>
  )
}

function Panel({
  title,
  subtitle,
  drafts,
  siteId,
  emptyHint,
}: {
  title: string
  subtitle: string
  drafts: AttentionDraft[] | null
  siteId: string
  emptyHint: string
}) {
  return (
    <section style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem' }}>
      <h2 style={{ fontSize: '1rem', margin: 0 }}>{title}</h2>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginBottom: '0.75rem' }}>{subtitle}</div>
      {drafts === null ? (
        <div className="loading">Loading…</div>
      ) : drafts.length === 0 ? (
        <div style={{ color: 'var(--text-faint)', fontSize: '0.875rem' }}>{emptyHint}</div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {drafts.map((d) => (
            <li
              key={d.slug}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <Link to={`/sites/${siteId}/articles/${d.slug}`} style={{ fontWeight: 500 }}>
                <StatusDot status={d.status} /> {d.title}
              </Link>
              <span style={{ color: 'var(--text-faint)', fontSize: '0.8rem' }}>
                {d.lintScore !== null && <>lint {d.lintScore} · </>}
                {d.wordCount.toLocaleString()}w
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function IndexingPanel({
  health,
  error,
  resubmitBusy,
  recentSuccess,
  onResubmit,
}: {
  health: IndexingHealth | null
  error: string | null
  resubmitBusy: string | null
  recentSuccess: Record<string, number>
  onResubmit: (slug: string) => void
}) {
  if (error) {
    return (
      <section
        style={{
          background: 'var(--bg-elev)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1rem 1.25rem',
        }}
      >
        <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Index health</h2>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{error}</div>
      </section>
    )
  }
  if (!health) return null
  const r = health.rollup
  const notIndexed = health.rows.filter((row) => row.state !== 'indexed')

  return (
    <section
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem 1.25rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>Index health</h2>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
          {r.total} published · {r.indexed} indexed · {r.discovered + r.unknown} pending · {r.excluded} excluded
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.85rem', fontSize: '0.7rem', color: 'var(--text-faint)', marginBottom: '0.75rem' }}>
        <span><span style={{ color: 'var(--ok)' }}>✓</span> indexed — Google shows it in results</span>
        <span><span style={{ color: 'var(--warn)' }}>⏳</span> pending — discovered, not yet picked</span>
        <span><span style={{ color: 'var(--danger)' }}>✗</span> excluded — Google declined</span>
        <span><span style={{ color: 'var(--text-faint)' }}>?</span> unknown — not yet inspected</span>
      </div>

      {/* tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        <Tile label="Indexed" value={r.indexed} tone="ok" />
        <Tile label="Pending" value={r.discovered + r.unknown} tone="warn" />
        <Tile label="Excluded" value={r.excluded} tone={r.excluded > 0 ? 'danger' : 'neutral'} />
        <Tile label="Canonical issues" value={r.canonicalMismatches} tone={r.canonicalMismatches > 0 ? 'danger' : 'neutral'} />
      </div>

      {/* canonical mismatches — the actionable silent killer */}
      <CanonicalIssuesPanel rows={health.rows} />

      {/* not-indexed list */}
      {notIndexed.length === 0 ? (
        <div style={{ color: 'var(--ok)', fontSize: '0.875rem' }}>Every published article is indexed. 🎯</div>
      ) : (
        <>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            Not yet indexed ({notIndexed.length})
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {notIndexed.slice(0, 12).map((row) => (
              <li
                key={row.slug}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderTop: '1px solid var(--border)',
                  fontSize: '0.875rem',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, paddingRight: '0.5rem' }}>
                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <IndexBadge state={row.state} /> {row.title}
                  </div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '0.75rem' }}>
                    {row.indexingApiAt
                      ? `submitted ${ago(row.indexingApiAt)}`
                      : 'never submitted to Indexing API'}
                    {row.inspectedAt && ` · inspected ${ago(row.inspectedAt)}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.3rem 0.6rem',
                    background: recentSuccess[row.slug] ? '#dcfce7' : undefined,
                    color: recentSuccess[row.slug] ? '#166534' : undefined,
                    borderColor: recentSuccess[row.slug] ? '#86efac' : undefined,
                    transition: 'background 0.2s, color 0.2s',
                  }}
                  disabled={resubmitBusy === row.slug || !!recentSuccess[row.slug]}
                  onClick={() => onResubmit(row.slug)}
                >
                  {resubmitBusy === row.slug
                    ? 'Resubmitting…'
                    : recentSuccess[row.slug]
                      ? '✓ Submitted'
                      : 'Resubmit'}
                </button>
              </li>
            ))}
          </ul>
          {notIndexed.length > 12 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.5rem' }}>
              + {notIndexed.length - 12} more — sweep daily via cron
            </div>
          )}
        </>
      )}
    </section>
  )
}

function CanonicalIssuesPanel({ rows }: { rows: IndexingRow[] }) {
  const issues = rows.filter(
    (r) =>
      r.canonicalGoogle &&
      r.canonicalUser &&
      stripTrailingSlashAndQuery(r.canonicalGoogle) !== stripTrailingSlashAndQuery(r.canonicalUser),
  )
  if (issues.length === 0) return null

  // Group by what Google chose, so we see if the same canonical pulls many articles.
  const byTarget = new Map<string, IndexingRow[]>()
  for (const r of issues) {
    const target = r.canonicalGoogle!
    if (!byTarget.has(target)) byTarget.set(target, [])
    byTarget.get(target)!.push(r)
  }
  const groups = [...byTarget.entries()].sort((a, b) => b[1].length - a[1].length)

  return (
    <details
      style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 'var(--radius)',
        padding: '0.5rem 0.75rem',
        margin: '0.75rem 0',
      }}
      open
    >
      <summary style={{ cursor: 'pointer', fontWeight: 500, color: 'var(--danger)' }}>
        ⚠ {issues.length} canonical mismatch{issues.length === 1 ? '' : 'es'} — Google picked a different URL than you declared
      </summary>
      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        Each row: your article → the URL Google chose instead. Same target across multiple rows = likely an internal-link or sitemap issue routing readers to one URL while Google sees another.
      </div>
      {groups.map(([target, articles]) => (
        <div key={target} style={{ marginTop: '0.75rem', borderTop: '1px solid #fecaca', paddingTop: '0.5rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Google chose canonical →{' '}
            <a href={target} target="_blank" rel="noopener" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
              {prettyUrl(target)}
            </a>
            {' '}for {articles.length} article{articles.length === 1 ? '' : 's'}:
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {articles.map((r) => (
              <li
                key={r.slug}
                style={{
                  padding: '0.3rem 0.5rem',
                  borderRadius: 'var(--radius)',
                  background: 'rgba(255,255,255,0.7)',
                  fontSize: '0.8rem',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ color: 'var(--text)' }}>{r.title}</strong>{' '}
                  <span style={{ color: 'var(--text-faint)' }}>· {r.coverageState ?? '?'}</span>
                </span>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener"
                  style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textDecoration: 'underline' }}
                >
                  view yours
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </details>
  )
}

function LinksPanel({
  siteId,
  health,
  error,
}: {
  siteId: string
  health: LinksHealth | null
  error: string | null
}) {
  if (error) {
    return (
      <section style={panelShell}>
        <h2 style={panelTitle}>Internal links</h2>
        <div className="error-state">{error}</div>
      </section>
    )
  }
  if (!health) {
    return (
      <section style={panelShell}>
        <h2 style={panelTitle}>Internal links</h2>
        <div className="loading">Loading…</div>
      </section>
    )
  }
  const orphans = health.rows.filter((r) => r.isOrphan)

  return (
    <section style={panelShell}>
      <h2 style={panelTitle}>Internal links</h2>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginBottom: '0.75rem' }}>
        Cross-linking spreads page authority. Orphans (no factory article links to them) leak SEO value.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <Tile label="Articles" value={health.totals.articles} tone="neutral" />
        <Tile
          label="Orphans"
          value={health.totals.orphans}
          tone={health.totals.orphans === 0 ? 'ok' : health.totals.orphans > 5 ? 'danger' : 'warn'}
        />
        <Tile
          label="Hub-only"
          value={health.totals.hubOnly}
          tone={health.totals.hubOnly > 10 ? 'warn' : 'neutral'}
        />
        <Tile label="Cross-linked" value={health.totals.crossLinked} tone="ok" />
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', marginBottom: '0.5rem' }}>
        Avg outbound (factory): <strong>{health.totals.avgOutbound}</strong> ·
        Avg inbound: <strong>{health.totals.avgInbound}</strong>
      </div>

      {orphans.length === 0 ? (
        <div style={{ color: 'var(--text-faint)', fontSize: '0.85rem' }}>
          ✓ No orphans — every article is linked to by at least one other.
        </div>
      ) : (
        <details open>
          <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
            {orphans.length} orphan{orphans.length === 1 ? '' : 's'} — nothing links to these
          </summary>
          <ul style={{ listStyle: 'none', margin: '0.5rem 0 0 0', padding: 0 }}>
            {orphans.slice(0, 15).map((r) => (
              <li
                key={r.slug}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.35rem 0',
                  borderTop: '1px solid var(--border)',
                  fontSize: '0.8rem',
                }}
              >
                <Link to={`/sites/${siteId}/articles/${r.slug}`} style={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                  {r.title}
                </Link>
                <span style={{ color: 'var(--text-faint)', fontSize: '0.75rem' }}>
                  outbound {r.outbound.length} · inbound 0
                </span>
              </li>
            ))}
          </ul>
          {orphans.length > 15 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.5rem' }}>
              + {orphans.length - 15} more
            </div>
          )}
        </details>
      )}
    </section>
  )
}

const panelShell = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '1rem 1.25rem',
} as const
const panelTitle = { fontSize: '1rem', margin: 0 } as const

function stripTrailingSlashAndQuery(u: string): string {
  return (u.split('?')[0] || u).replace(/\/$/, '')
}

function prettyUrl(u: string): string {
  return u.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'ok' | 'warn' | 'danger' | 'neutral'
}) {
  const colors = {
    ok: { fg: 'var(--ok)', bg: '#f0fdf4' },
    warn: { fg: 'var(--warn)', bg: '#fffbeb' },
    danger: { fg: 'var(--danger)', bg: '#fef2f2' },
    neutral: { fg: 'var(--text-muted)', bg: 'var(--bg-muted)' },
  }[tone]
  return (
    <div style={{ background: colors.bg, padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.fg }}>{value}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  )
}

function IndexBadge({ state }: { state: IndexingRow['state'] }) {
  const map = {
    indexed: { label: '✓', color: 'var(--ok)' },
    discovered: { label: '⏳', color: 'var(--warn)' },
    excluded: { label: '✗', color: 'var(--danger)' },
    unknown: { label: '?', color: 'var(--text-faint)' },
  }[state]
  return <span style={{ color: map.color, marginRight: '0.4rem' }} title={state}>{map.label}</span>
}

function ago(unixSeconds: number): string {
  const diffH = (Date.now() / 1000 - unixSeconds) / 3600
  if (diffH < 1) return 'just now'
  if (diffH < 24) return `${Math.floor(diffH)}h ago`
  return `${Math.floor(diffH / 24)}d ago`
}

function CostTile({ cost }: { cost: CostSummary | null }) {
  if (!cost) return null
  const pct = Math.min(100, Math.round((cost.totalCents / cost.capCents) * 100))
  return (
    <section
      style={{
        background: cost.blocked ? '#fef2f2' : 'var(--bg-elev)',
        border: `1px solid ${cost.blocked ? 'var(--danger)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '1rem 1.25rem',
      }}
    >
      <h2 style={{ fontSize: '1rem', margin: 0, display: 'flex', justifyContent: 'space-between' }}>
        <span>Today's cost</span>
        <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
          ${(cost.totalCents / 100).toFixed(2)} / ${(cost.capCents / 100).toFixed(0)}
        </span>
      </h2>
      <div style={{ height: 6, background: 'var(--bg-muted)', borderRadius: 3, marginTop: '0.75rem', overflow: 'hidden' }}>
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: cost.blocked ? 'var(--danger)' : 'var(--accent)',
            transition: 'width 200ms',
          }}
        />
      </div>
      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-faint)' }}>
        {cost.blocked ? 'Daily cap hit. New generations blocked.' : `${pct}% of $50/day cap`}
        {cost.perProvider.length > 0 && (
          <> · {cost.perProvider.map((p) => `${p.provider} ${p.calls}`).join(' · ')}</>
        )}
      </div>
    </section>
  )
}
